/**
 * Pure geometry for the drag-to-zoom selection shared by the time-series and XY
 * graph windows. Kept free of uPlot/DOM so it can be unit-tested; the DOM wiring
 * lives in edgeClampZoomPlugin.ts.
 */

/** Clamp a pixel position to the plot area [0, dim]. A pointer that overshoots a
 *  border therefore reads as the edge (the maximum/minimum) rather than a value
 *  outside the plot. */
export function clampToPlot(px: number, dim: number): number {
  if (!(dim > 0)) return px < 0 ? 0 : px;
  return px < 0 ? 0 : px > dim ? dim : px;
}

export interface ZoomSelection {
  /** Left edge of the selection box, in plot pixels. */
  left: number;
  /** Top edge of the selection box, in plot pixels. */
  top: number;
  /** Selection width in pixels (already clamped to the plot). */
  width: number;
  /** Selection height in pixels (already clamped to the plot). */
  height: number;
  /** True when the horizontal drag is large enough to zoom the x-axis. */
  zoomX: boolean;
  /** True when the vertical drag is large enough to zoom the y-axis. */
  zoomY: boolean;
}

/**
 * Turn a raw drag (start → end, in plot pixels, either point possibly outside
 * the plot) into a normalised, edge-clamped selection box plus per-axis "should
 * zoom" flags. Overshooting a border clamps to that edge, so a selection can
 * always reach the plot boundary even on a fast drag past it.
 *
 * `minDragPx` filters out clicks / accidental micro-drags: an axis only zooms
 * when its span meets the threshold, which also prevents a purely horizontal
 * drag from collapsing the y-axis to a zero-height range (and vice versa).
 */
export function computeZoomSelection(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  plotW: number,
  plotH: number,
  minDragPx: number,
): ZoomSelection {
  const x0 = clampToPlot(startX, plotW);
  const x1 = clampToPlot(endX, plotW);
  const y0 = clampToPlot(startY, plotH);
  const y1 = clampToPlot(endY, plotH);
  const width = Math.abs(x1 - x0);
  const height = Math.abs(y1 - y0);
  return {
    left: Math.min(x0, x1),
    top: Math.min(y0, y1),
    width,
    height,
    zoomX: width >= minDragPx,
    zoomY: height >= minDragPx,
  };
}
